import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { DndContext, DragOverlay, pointerWithin, type DragStartEvent, type DragEndEvent } from "@dnd-kit/core";
import { LayoutTemplate, Trophy } from "lucide-react";
import type { EventPublic, RugbyEventMatchResultSummary, MatchPublic, MatchTeamPublic, SchoolSportPublic, SchoolOpponentPublic, PublishScheduleConflict } from "@dugads/shared";
import { findScheduleConflicts, SCHEDULE_CONFLICTS_CODE } from "@dugads/shared";
import * as api from "@/lib/api";
import { type DraggableTeam, fmtTime } from "@/lib/scheduler-utils";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useDndSensors } from "@/lib/dnd-sensors";
import { EventHeader } from "@/components/event-detail/event-header";
import { SchoolsManager } from "@/components/event-detail/schools-manager";
import { CourtsManager } from "@/components/event-detail/courts-manager";
import { TeamPanel } from "@/components/event-detail/team-panel";
import { ScheduleGrid, type ScheduleGridHandle } from "@/components/event-detail/schedule-grid";
import { VenuesManager } from "@/components/event-detail/venues-manager";
import { ParticipatingTeamsSection } from "@/components/event-detail/participating-teams";
import { TeamAnnouncementsSection } from "@/components/event-detail/team-announcements";
import { AvailabilitySection } from "@/components/event-detail/availability-section";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { OfflineReadBanner } from "@/components/offline/offline-read-banner";
import { useMediaQuery } from "@/lib/use-media-query";
import { MobileSchedule } from "@/components/event-detail/mobile-schedule";
import { SaveTemplateDialog } from "@/components/event-detail/save-template-dialog";
import { ApplyTemplateDialog } from "@/components/event-detail/apply-template-dialog";
import { EventInfoPanel } from "@/components/event-detail/info-panel";
import { EventBoard } from "@/components/event-detail/event-board";

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery("(min-width: 768px)");

  // ─── Tab state ────────────────────────────────────────────────
  // Two workflows on one page: "plan" = build the fixture (schools/courts/
  // venues/teams/schedule); "matchday" = run it (announcements, availability,
  // logistics, results). Driven by ?tab= so it's shareable and survives reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: "plan" | "matchday" | "info" | "board" =
    tabParam === "matchday" ? "matchday" : tabParam === "info" ? "info" : tabParam === "board" ? "board" : "plan";
  function selectTab(tab: "plan" | "matchday" | "info" | "board") {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "plan") next.delete("tab");
        else next.set("tab", tab);
        return next;
      },
      { replace: true },
    );
  }

  // ─── Core data ────────────────────────────────────────────────
  const [event, setEvent] = useState<EventPublic | null>(null);
  const [matches, setMatches] = useState<MatchPublic[]>([]);
  const [matchTeams, setMatchTeams] = useState<Record<string, MatchTeamPublic[]>>({});
  const [matchResults, setMatchResults] = useState<Record<string, RugbyEventMatchResultSummary>>({});
  const [matchFlashes, setMatchFlashes] = useState<Record<string, { type: string; side: string; points: number; at: number }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─── Reference data ───────────────────────────────────────────
  const [orgSchools, setOrgSchools] = useState<{ id: string; name: string }[]>([]);
  const [schoolSports, setSchoolSports] = useState<SchoolSportPublic[]>([]);
  const [hostSchoolCourts, setHostSchoolCourts] = useState<{ id: string; name: string }[]>([]);
  const [opponents, setOpponents] = useState<SchoolOpponentPublic[]>([]);

  // ─── Team panel data ──────────────────────────────────────────
  const [schoolTeams, setSchoolTeams] = useState<Record<string, DraggableTeam[]>>({});

  // ─── 184: scheduler drag-and-drop (team panel + grid in one DndContext) ──
  const schedulerSensors = useDndSensors();
  const scheduleGridRef = useRef<ScheduleGridHandle>(null);
  const [activeDragLabel, setActiveDragLabel] = useState<string | null>(null);
  // 191 -- non-null opens the publish schedule-conflict warning dialog.
  const [publishConflicts, setPublishConflicts] = useState<PublishScheduleConflict[] | null>(null);
  // 192 -- match day template dialogs (save the grid / start from a template).
  const [templateDialog, setTemplateDialog] = useState<"save" | "apply" | null>(null);

  // ─── Reload: single function, single source of truth ──────────

  const loadMatchTeams = useCallback(async (matchList: MatchPublic[]) => {
    const results = await Promise.all(
      matchList.map((m) => api.getMatchTeams(m.id).then((res) => [m.id, res.teams] as const)),
    );
    const map: Record<string, MatchTeamPublic[]> = {};
    for (const [matchId, t] of results) map[matchId] = t;
    setMatchTeams(map);
  }, []);

  /** Full reload -- initial load + school/court changes */
  const reload = useCallback(() => {
    if (!id) return;
    if (!event) setLoading(true);
    Promise.all([api.getEvent(id), api.getMatches(id)])
      .then(async ([eventRes, matchesRes]) => {
        setEvent(eventRes.event);
        setMatches(matchesRes.matches);
        await loadMatchTeams(matchesRes.matches);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load event"))
      .finally(() => setLoading(false));
  }, [id, loadMatchTeams]);

  /** Light refresh -- only matches + teams, no event refetch, no loading state.
   *  Used as background sync after optimistic updates. */
  const refreshMatches = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.getMatches(id);
      setMatches(res.matches);
      await loadMatchTeams(res.matches);
    } catch { /* silent -- optimistic state is good enough */ }
  }, [id, loadMatchTeams]);

  useEffect(() => { reload(); }, [reload]);

  // 192 -- event-create can hand off with ?applyTemplate=1 to open the
  // start-from-template picker as soon as the fresh (empty) draft loads.
  const wantsApplyTemplate = searchParams.get("applyTemplate") === "1";
  const loadedEventId = event?.id ?? null;
  const loadedCanManageMatches = event?._actions.includes("manage_matches") ?? false;
  useEffect(() => {
    if (!wantsApplyTemplate || loading || !loadedEventId) return;
    if (matches.length === 0 && loadedCanManageMatches) {
      setTemplateDialog("apply");
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("applyTemplate");
        return next;
      },
      { replace: true },
    );
  }, [wantsApplyTemplate, loading, loadedEventId, loadedCanManageMatches, matches.length, setSearchParams]);

  // Live scores -- one initial fetch for the snapshot, then an SSE subscription
  // receives deltas pushed by the server on every mutation. No polling.
  useEffect(() => {
    if (!id || !event) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await api.getRugbyEventResults(id);
        if (cancelled) return;
        const byId: Record<string, RugbyEventMatchResultSummary> = {};
        for (const m of res.matches) byId[m.match_id] = m;
        setMatchResults(byId);
      } catch { /* non-critical */ }
    })();

    const stop = api.subscribeRugbyScoreStream(id, (update) => {
      setMatchResults((prev) => {
        const existing = prev[update.match_id];
        const merged: RugbyEventMatchResultSummary = existing
          ? {
              ...existing,
              home_score: update.home_score,
              away_score: update.away_score,
              final: update.final,
            }
          : {
              match_id: update.match_id,
              match_name: "",
              starts_at: "",
              home_team: null,
              away_team: null,
              home_score: update.home_score,
              away_score: update.away_score,
              final: update.final,
              winner_side: null,
              my_child_playing: false,
            };
        return { ...prev, [update.match_id]: merged };
      });
      if (update.last_event_type && update.last_team_side && update.last_points != null) {
        setMatchFlashes((prev) => ({
          ...prev,
          [update.match_id]: {
            type: update.last_event_type!,
            side: update.last_team_side!,
            points: update.last_points!,
            at: Date.now(),
          },
        }));
      }
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, [id, event?.id]);

  // ─── Reference data loading ───────────────────────────────────

  useEffect(() => {
    if (!event) return;
    // Load org schools -- the server filters by the user's org
    api.getSchools().then((r) =>
      setOrgSchools(r.schools.map((s) => ({ id: s.id, name: s.name }))),
    ).catch(() => {});
    api.getSchoolSports(event.school_id).then((res) => setSchoolSports(res.sports)).catch(() => {});
    api.getSchoolOpponents(event.school_id).then((res) => setOpponents(res.opponents)).catch(() => {});
  }, [event?.school_id]);

  const hostSchool = event?.schools.find((s) => s.is_host);
  useEffect(() => {
    if (hostSchool?.school_id) {
      api.getCourts(hostSchool.school_id).then((res) =>
        setHostSchoolCourts(res.courts.map((c) => ({ id: c.id, name: c.name }))),
      ).catch(() => setHostSchoolCourts([]));
    } else {
      setHostSchoolCourts([]);
    }
  }, [hostSchool?.school_id]);

  // ─── Derived data ─────────────────────────────────────────────

  const canEdit = event?._actions.includes("edit") ?? false;
  const canManageMatches = event?._actions.includes("manage_matches") ?? false;

  const sortedSchools = useMemo(() => {
    if (!event) return [];
    return [...event.schools].sort((a, b) => {
      if (a.is_host !== b.is_host) return a.is_host ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [event?.schools]);

  const courtLabel = useMemo(() => {
    if (!event?.sport_id || !schoolSports.length) return "Court";
    return schoolSports.find((s) => s.sport_id === event.sport_id)?.court_type_name ?? "Court";
  }, [event?.sport_id, schoolSports]);

  // Count how many matches each team is in
  const teamMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    function inc(key: string) { counts.set(key, (counts.get(key) ?? 0) + 1); }
    // Dugads teams: count by team_id (unique, no cross-school collision)
    for (const teams of Object.values(matchTeams)) {
      for (const t of teams) inc(t.team_id);
    }
    // External teams: count by name, but ONLY for sides without a MatchTeam record
    for (const m of matches) {
      const mt = matchTeams[m.id] ?? [];
      const hasHome = mt.some((t) => t.role === "home");
      const hasAway = mt.some((t) => t.role === "away");
      const [left, right] = m.name.split(" vs ");
      if (!hasHome && left && left !== "TBD") inc(left);
      if (!hasAway && right && right !== "TBD") inc(right);
    }
    return counts;
  }, [matches, matchTeams]);

  const teamColorMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const [, teams] of Object.entries(schoolTeams)) {
      for (const t of teams) {
        map.set(t.id, t.colorIndex);
        if (t.teamId) map.set(t.teamId, t.colorIndex);
        map.set(t.name, t.colorIndex);
        // Ad-hoc teams: also key by "SchoolName TeamName" (used in match names)
        if (!t.teamId) map.set(`${t.schoolName} ${t.name}`, t.colorIndex);
      }
    }
    return map;
  }, [schoolTeams]);

  // Map team id/name → school name (for displaying school on away pills)
  const teamSchoolMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const [, teams] of Object.entries(schoolTeams)) {
      for (const t of teams) {
        map.set(t.id, t.schoolName);
        if (t.teamId) map.set(t.teamId, t.schoolName);
        map.set(t.name, t.schoolName);
        if (!t.teamId) map.set(`${t.schoolName} ${t.name}`, t.schoolName);
      }
    }
    return map;
  }, [schoolTeams]);

  // ─── Load teams for participating schools ─────────────────────

  useEffect(() => {
    if (!event || !sortedSchools.length) return;
    const eventId = event.id;
    async function loadTeams() {
      // Aggregated endpoint -- returns teams from ALL participating schools
      // (including ones the current user doesn't directly manage). The API
      // already filters by the event's sport.
      let allTeams: Array<{ id: string; name: string; school_id: string; age_group_id: string | null; age_group_name: string | null; default_first_half_mins: number | null; default_break_mins: number | null; default_second_half_mins: number | null }> = [];
      try {
        const res = await api.getEventTeams(eventId);
        allTeams = res.teams;
      } catch { /* fall through -- ad-hoc teams below still render */ }

      const result: Record<string, DraggableTeam[]> = {};
      for (let ci = 0; ci < sortedSchools.length; ci++) {
        const s = sortedSchools[ci];
        const dbTeams = allTeams
          .filter((t) => t.school_id === s.school_id)
          .map((t) => ({
            id: t.id, name: t.name, schoolName: s.name, teamId: t.id as string | null, ageGroupId: t.age_group_id, ageGroupName: t.age_group_name, defaultFirstHalfMins: t.default_first_half_mins, defaultBreakMins: t.default_break_mins, defaultSecondHalfMins: t.default_second_half_mins, colorIndex: ci,
          }));
        const dbNames = new Set(dbTeams.map((t) => t.name));
        const seen = new Set<string>();
        const extTeams = (s.team_names ?? [])
          .filter((tn) => {
            if (dbNames.has(tn) || seen.has(tn)) return false;
            seen.add(tn);
            return true;
          })
          .map((tn) => ({
            id: `ext-${s.name}-${tn}`, name: tn, schoolName: s.name, teamId: null as string | null, ageGroupId: null, ageGroupName: null, defaultFirstHalfMins: null, defaultBreakMins: null, defaultSecondHalfMins: null, colorIndex: ci,
          }));
        result[s.name] = [...dbTeams, ...extTeams];
      }
      setSchoolTeams(result);
    }
    loadTeams();
  }, [sortedSchools, event?.id, event?.sport_id]);

  // ─── Callbacks ────────────────────────────────────────────────

  async function updateField(field: string, value: unknown) {
    if (!event) return;
    notifySaving();
    const res = await api.updateEvent(event.id, { [field]: value });
    setEvent(res.event);
    notifySaved();
  }

  function handleSchedulerDragStart(e: DragStartEvent) {
    const label = e.active.data.current?.label;
    setActiveDragLabel(typeof label === "string" ? label : null);
  }

  // The grid owns all the match state + drop logic; it exposes a single
  // onDragEnd via its ref so this DndContext can route the drop into it.
  function handleSchedulerDragEnd(e: DragEndEvent) {
    setActiveDragLabel(null);
    scheduleGridRef.current?.onDragEnd(e);
  }

  async function handleAddExtTeam(schoolName: string, teamName: string) {
    if (!event) return;
    const schoolEntry = event.schools.find((s) => s.name === schoolName);
    if (!schoolEntry) return;

    // Optimistic local update
    const ci = sortedSchools.findIndex((s) => s.name === schoolName);
    setSchoolTeams((prev) => {
      const existing = prev[schoolName] ?? [];
      if (existing.some((t) => t.name === teamName)) return prev;
      return {
        ...prev,
        [schoolName]: [...existing, { id: `ext-${schoolName}-${teamName}-${Date.now()}`, name: teamName, schoolName, teamId: null, ageGroupId: null, ageGroupName: null, defaultFirstHalfMins: null, defaultBreakMins: null, defaultSecondHalfMins: null, colorIndex: ci >= 0 ? ci : 0 }],
      };
    });

    // Persist
    const newNames = [...(schoolEntry.team_names ?? []), teamName];
    await api.updateEventSchool(event.id, schoolEntry.id, { team_names: newNames });
    reload();
  }

  async function handleRenameExtTeam(schoolName: string, oldName: string, newName: string) {
    if (!event) return;
    const schoolEntry = event.schools.find((s) => s.name === schoolName);
    if (!schoolEntry) return;

    // Optimistic: update team panel
    setSchoolTeams((prev) => ({
      ...prev,
      [schoolName]: (prev[schoolName] ?? []).map((t) =>
        t.name === oldName ? { ...t, name: newName, id: `ext-${schoolName}-${newName}` } : t,
      ),
    }));

    // Optimistic: update match names that reference the old team name
    const affectedMatches = matches.filter((m) => {
      const [left, right] = m.name.split(" vs ");
      return left === oldName || right === oldName;
    });
    if (affectedMatches.length > 0) {
      setMatches((prev) => prev.map((m) => {
        const [left, right] = m.name.split(" vs ");
        if (left === oldName || right === oldName) {
          const newLeft = left === oldName ? newName : left;
          const newRight = right === oldName ? newName : right;
          return { ...m, name: `${newLeft} vs ${newRight}` };
        }
        return m;
      }));
    }

    // Persist team_names
    const updatedNames = (schoolEntry.team_names ?? []).map((n) => n === oldName ? newName : n);
    await api.updateEventSchool(event.id, schoolEntry.id, { team_names: updatedNames });

    // Persist affected match names
    for (const m of affectedMatches) {
      const [left, right] = m.name.split(" vs ");
      const newLeft = left === oldName ? newName : left;
      const newRight = right === oldName ? newName : right;
      await api.updateMatch(m.id, { name: `${newLeft} vs ${newRight}` });
    }

    refreshMatches();
  }

  async function handleDeleteExtTeam(schoolName: string, teamName: string) {
    if (!event) return;
    const schoolEntry = event.schools.find((s) => s.name === schoolName);
    if (!schoolEntry) return;

    // Optimistic: remove from team panel
    setSchoolTeams((prev) => ({
      ...prev,
      [schoolName]: (prev[schoolName] ?? []).filter((t) => t.name !== teamName),
    }));

    // Persist: remove from team_names
    const updatedNames = (schoolEntry.team_names ?? []).filter((n) => n !== teamName);
    await api.updateEventSchool(event.id, schoolEntry.id, { team_names: updatedNames });
    reload();
  }

  async function handleDelete() {
    if (!event || !confirm("Are you sure you want to delete this event?")) return;
    try {
      await api.deleteEvent(event.id);
      navigate(`/schools/${event.school_id}/events`);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete");
    }
  }

  // 080 -- lifecycle actions
  // 191 -- publish warns (never blocks) on same-field schedule overlaps. The
  // conflicts are computed client-side from loaded state via the shared
  // utility; the server 409 (code: schedule_conflicts) is the stale-client
  // backstop and feeds the same dialog.
  function buildPublishConflicts(): PublishScheduleConflict[] {
    if (!event) return [];
    const byId = new Map(matches.map((m) => [m.id, m]));
    const courtNames = new Map(event.courts.map((c) => [c.id, c.name]));
    return findScheduleConflicts(matches).map((p) => {
      const a = byId.get(p.a_id)!;
      const b = byId.get(p.b_id)!;
      return {
        event_court_id: p.event_court_id,
        court_name: courtNames.get(p.event_court_id) ?? null,
        a: { id: a.id, name: a.name, starts_at: a.starts_at, ends_at: a.ends_at },
        b: { id: b.id, name: b.name, starts_at: b.starts_at, ends_at: b.ends_at },
      };
    });
  }

  async function doPublish(acknowledgeConflicts: boolean) {
    if (!event) return;
    try {
      const res = await api.publishEvent(event.id, acknowledgeConflicts ? { acknowledge_conflicts: true } : undefined);
      setEvent(res.event);
      setPublishConflicts(null);
    } catch (e) {
      if (e instanceof api.ApiError && e.status === 409 && e.body?.code === SCHEDULE_CONFLICTS_CODE) {
        setPublishConflicts((e.body.conflicts as PublishScheduleConflict[] | undefined) ?? []);
        return;
      }
      setPublishConflicts(null);
      setError(e instanceof api.ApiError ? e.message : "Failed to publish");
    }
  }

  async function handlePublish() {
    if (!event) return;
    const conflicts = buildPublishConflicts();
    if (conflicts.length > 0) {
      setPublishConflicts(conflicts);
      return;
    }
    if (!confirm("Publish this event? Parents and coaches will be notified.")) return;
    await doPublish(false);
  }

  async function handleUnpublish() {
    if (!event) return;
    if (!confirm("Revert to draft? Only available within 1 hour of publishing.")) return;
    try {
      const res = await api.unpublishEvent(event.id);
      setEvent(res.event);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to unpublish");
    }
  }

  async function handleCancel() {
    if (!event) return;
    const reason = prompt("Reason for cancelling this event? (required, shown to parents)");
    if (!reason || !reason.trim()) return;
    try {
      const res = await api.cancelEvent(event.id, { reason: reason.trim() });
      setEvent(res.event);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to cancel");
    }
  }

  // ─── Render ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col gap-6" data-page="event-detail" data-state="loading">
        <div className="flex animate-pulse flex-col gap-3">
          <div className="h-4 w-48 rounded bg-muted" />
          <div className="h-8 w-64 rounded bg-muted" />
          <div className="h-4 w-32 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error && !event) return <p className="text-sm text-destructive" data-page="event-detail" data-state="error" data-error>{error}</p>;
  if (!event) return <p className="text-muted-foreground" data-page="event-detail" data-state="empty">Event not found</p>;

  return (
    <div className="flex flex-col gap-4" data-page="event-detail" data-state={error ? "error" : "ready"} data-tab={activeTab}>
      <OfflineReadBanner />
      <EventHeader
        event={event}
        canEdit={canEdit}
        onUpdate={updateField}
        onDelete={handleDelete}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onCancel={handleCancel}
        onSaveAsTemplate={canManageMatches && matches.length > 0 ? () => setTemplateDialog("save") : undefined}
      />

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {/* Plan vs Match Day -- two distinct workflows on one page. Full-width
          segmented control on mobile; underline tabs on desktop. 198: sticky
          under the mobile top bar so switching tabs after scrolling does not
          require scrolling back up (the muted canvas makes the wrapper opaque). */}
      <nav
        className="sticky top-0 z-20 flex rounded-xl bg-muted p-0.5 md:static md:z-auto md:gap-1 md:rounded-none md:bg-transparent md:p-0 md:border-b md:border-border"
        role="tablist"
        aria-label="Event view"
      >
        {([
          { key: "plan", label: "Plan" },
          { key: "matchday", label: "Match Day" },
          { key: "info", label: "Info" },
          { key: "board", label: "Board" },
        ] as const).map((t) => {
          const selected = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              data-tab={t.key}
              data-active={selected}
              onClick={() => selectTab(t.key)}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition md:-mb-px md:flex-none md:rounded-none md:px-4 ${
                selected
                  ? "bg-card text-foreground shadow-sm md:bg-transparent md:shadow-none md:border-b-2 md:border-primary"
                  : "text-muted-foreground hover:text-foreground md:border-b-2 md:border-transparent"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* ── Plan: build the fixture ─────────────────────────────── */}
      {activeTab === "plan" && (
        <div className="flex flex-col gap-4" data-region="plan">
          {/* Tournament bracket entry -- tournament events only */}
          {event.event_type === "tournament" && (
            <Link
              to={`/events/${event.id}/tournament`}
              data-action="open-tournament"
              className="inline-flex w-fit items-center gap-2 rounded-md border border-input px-3 py-2 text-sm font-medium text-foreground transition hover:border-primary"
            >
              <Trophy className="h-4 w-4 text-primary" aria-hidden /> Tournament bracket
            </Link>
          )}

          {/* Schools + Courts + Venues -- stacked on mobile, row on desktop */}
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            <SchoolsManager event={event} sortedSchools={sortedSchools} canEdit={canEdit} orgSchools={orgSchools} opponents={opponents} setEvent={setEvent} />
            <CourtsManager event={event} canEdit={canEdit} courtLabel={courtLabel} hostSchoolCourts={hostSchoolCourts} setEvent={setEvent} />
            <VenuesManager event={event} canEdit={canEdit} setEvent={setEvent} />
          </div>

          {/* 101 -- Participating teams, derived from match assignments */}
          <ParticipatingTeamsSection matchTeams={matchTeams} />

          {/* 192 -- an empty day can start from a saved layout ("Save as
              template" for a built day lives in the header action row).
              Layout only, never teams. */}
          {canManageMatches && matches.length === 0 && (
            <div className="flex flex-wrap items-center gap-2" data-region="template-actions">
              <Button type="button" size="sm" data-action="start-from-template" onClick={() => setTemplateDialog("apply")}>
                <LayoutTemplate className="mr-2 h-4 w-4" aria-hidden />
                Start from template
              </Button>
            </div>
          )}

          {/* Schedule. Desktop: the drag-and-drop court x time grid (184 touch
              sensors keep it usable on tablets). Mobile: a vertical per-court
              match list (read + reschedule/edit), since the wide grid does not
              fit a phone. */}
          {isMdUp ? (
            <DndContext
              sensors={schedulerSensors}
              collisionDetection={pointerWithin}
              onDragStart={handleSchedulerDragStart}
              onDragEnd={handleSchedulerDragEnd}
            >
              <div className="flex h-[70vh] min-h-[420px] overflow-hidden rounded-lg border border-border" data-region="schedule">
                {canManageMatches && (
                  <TeamPanel
                    sortedSchools={sortedSchools}
                    schoolTeams={schoolTeams}
                    teamMatchCounts={teamMatchCounts}
                    onAddExtTeam={handleAddExtTeam}
                    onRenameExtTeam={handleRenameExtTeam}
                    onDeleteExtTeam={handleDeleteExtTeam}
                  />
                )}
                <ScheduleGrid
                  ref={scheduleGridRef}
                  event={event}
                  matches={matches}
                  matchTeams={matchTeams}
                  matchResults={matchResults}
                  matchFlashes={matchFlashes}
                  teamColorMap={teamColorMap}
                  teamSchoolMap={teamSchoolMap}
                  canManageMatches={canManageMatches}
                  courtLabel={courtLabel}
                  setMatches={setMatches}
                  setMatchTeams={setMatchTeams}
                  refreshMatches={refreshMatches}
                  reloadEvent={reload}
                />
              </div>
              <DragOverlay>
                {activeDragLabel ? (
                  <div className="pointer-events-none rounded border border-primary bg-background px-2 py-1 text-xs font-medium shadow-lg">
                    {activeDragLabel}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <MobileSchedule
              matches={matches}
              matchTeams={matchTeams}
              courtLabel={courtLabel}
              canManageMatches={canManageMatches}
              eventId={event.id}
              venues={event.venues}
              setMatches={setMatches}
              refreshMatches={refreshMatches}
            />
          )}
        </div>
      )}

      {/* ── Match Day: run the fixture ──────────────────────────── */}
      {activeTab === "matchday" && (
        <div className="flex flex-col gap-4" data-region="matchday">
          {/* 108 -- Team announcements (read; publishing is on the team page) */}
          <TeamAnnouncementsSection eventId={event.id} />

          {/* 138/141 -- Match availability + match-day actions (logistics,
              results). Each widget self-hides on 403. */}
          <AvailabilitySection matches={matches} matchTeams={matchTeams} />
        </div>
      )}

      {/* ── Info: the mobile-first event info pack (196) ────────────
          Gate times, accommodation, travel, etc. Organisers author here;
          the same blocks (visible only) render on the public tournament page. */}
      {activeTab === "info" && (
        <div className="flex flex-col gap-4" data-region="info-tab">
          <EventInfoPanel eventId={event.id} venues={event.venues} />
        </div>
      )}

      {/* ── Board: the per-event message board (197) ───────────────
          One authoritative announcement stream: organisers post, all
          participants read. Self-hides for non-participants. */}
      {activeTab === "board" && (
        <div className="flex flex-col gap-4" data-region="board-tab">
          <EventBoard eventId={event.id} />
        </div>
      )}

      {/* 192 -- match day template dialogs */}
      {templateDialog === "save" && (
        <SaveTemplateDialog
          eventId={event.id}
          matchCount={matches.length}
          onClose={() => setTemplateDialog(null)}
        />
      )}
      {templateDialog === "apply" && (
        <ApplyTemplateDialog
          event={event}
          onApplied={reload}
          onClose={() => setTemplateDialog(null)}
        />
      )}

      {/* 191 -- publish schedule-conflict warning. Warn-and-confirm, never a
          hard block: the organiser can publish anyway (a deliberate
          double-header on one field may be legitimate). 186 bottom-sheet
          pattern below sm. */}
      {publishConflicts !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
          data-dialog="publish-conflicts"
        >
          <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
            <CardHeader>
              <CardTitle>Overlapping matches on the same {courtLabel.toLowerCase()}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                These matches overlap. You can publish anyway, or go back and adjust the schedule
                first. Parents and coaches will be notified when the event publishes.
              </p>
              <ul className="flex flex-col gap-2">
                {publishConflicts.map((c) => (
                  <li
                    key={`${c.a.id}-${c.b.id}`}
                    className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm"
                    data-item="publish-conflict"
                  >
                    {c.court_name && <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.court_name}</p>}
                    <p>
                      <span className="font-medium">{c.a.name}</span>{" "}
                      <span className="text-muted-foreground">({fmtTime(c.a.starts_at)} - {fmtTime(c.a.ends_at)})</span>
                      {" "}overlaps{" "}
                      <span className="font-medium">{c.b.name}</span>{" "}
                      <span className="text-muted-foreground">({fmtTime(c.b.starts_at)} - {fmtTime(c.b.ends_at)})</span>
                    </p>
                  </li>
                ))}
              </ul>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" data-action="conflicts-go-back" onClick={() => setPublishConflicts(null)}>
                  Go back
                </Button>
                <Button type="button" data-action="publish-anyway" onClick={() => doPublish(true)}>
                  Publish anyway
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
